import { GenericColumn } from './tableColumns';
import { MoldOrder, Mold } from '../../../types/mold';
import { ClientCompanyCode, SimpleColumnConfig, SearchFieldConfig, getAppState } from '../../../models/appState';

/**
 * Table types for column configuration
 */
type TableType = 'moldOrders' | 'molds' | 'moldOrdersInMoldPage';

/**
 * Get column configuration for a specific company and table type
 * @param companyCode - The client company code (sbi/sk)
 * @param tableType - The table type ('moldOrders' or 'molds')
 * @returns Company-specific column configuration from centralized app state
 * @throws Error if company configuration is not found
 */
export const getTableColumnConfig = (
  companyCode: ClientCompanyCode, 
  tableType: TableType
): SimpleColumnConfig => {
  const appState = getAppState(companyCode);
  
  switch (tableType) {
    case 'moldOrders':
      return appState.table.moldOrdersColumns;
    case 'moldOrdersInMoldPage':
      return appState.table.moldOrdersInMoldPageColumns;
    case 'molds':
      return appState.table.moldColumns;
    default:
      throw new Error(`Unsupported table type: ${tableType}`);
  }
};

/**
 * Apply company-specific column configuration to base columns (generic)
 * @param allColumns - All available column definitions
 * @param companyCode - The client company code (sbi/sk)
 * @param tableType - The table type ('moldOrders' or 'molds')
 * @returns Filtered and customized columns for ColumnSelector, with custom labels applied
 * @description Returns only columns available to the company, in the configured order
 */
export const applyTableColumnConfiguration = <T>(
  allColumns: GenericColumn<T>[],
  companyCode: ClientCompanyCode,
  tableType: TableType
): GenericColumn<T>[] => {
  const config = getTableColumnConfig(companyCode, tableType);
  
  // Create a lookup map for fast access
  const columnMap = new Map(allColumns.map(col => [col.key, col]));
  
  // Return available columns in the configured order with custom labels applied
  return config.availableColumns
    .map(key => {
      const column = columnMap.get(key);
      if (!column) return null;
      
      // Apply custom label if provided
      return config.customLabels?.[key] 
        ? { ...column, label: config.customLabels[key] }
        : column;
    })
    .filter((col): col is GenericColumn<T> => col !== null);
};

/**
 * Helper for initializing user preferences with default visible columns (generic)
 * @param companyCode - The client company code (sbi/sk)
 * @param tableType - The table type ('moldOrders' or 'molds')
 * @returns Record mapping column keys to their default visibility state
 */
export const getDefaultVisibleColumnsForTable = (
  companyCode: ClientCompanyCode,
  tableType: TableType
): Record<string, boolean> => {
  const config = getTableColumnConfig(companyCode, tableType);
  
  // Create visibility map: all available columns default to false, then set defaults to true
  return config.availableColumns.reduce((acc, columnKey) => ({
    ...acc,
    [columnKey]: config.defaultVisibleColumns.includes(columnKey)
  }), {} as Record<string, boolean>);
};

// Simple aliases for common use cases
export const getMoldOrderColumnConfig = (companyCode: ClientCompanyCode): SimpleColumnConfig => {
  return getTableColumnConfig(companyCode, 'moldOrders');
};

export const getMoldColumnConfig = (companyCode: ClientCompanyCode): SimpleColumnConfig => {
  return getTableColumnConfig(companyCode, 'molds');
};

export const applyMoldOrderColumnConfiguration = (
  allColumns: GenericColumn<MoldOrder>[],
  companyCode: ClientCompanyCode
): GenericColumn<MoldOrder>[] => {
  return applyTableColumnConfiguration(allColumns, companyCode, 'moldOrders');
};

export const applyMoldColumnConfiguration = (
  allColumns: GenericColumn<Mold>[],
  companyCode: ClientCompanyCode
): GenericColumn<Mold>[] => {
  return applyTableColumnConfiguration(allColumns, companyCode, 'molds');
};

/*
 * Available column keys (for reference when configuring):
 *
 * Molds — ข้อมูลทั่วไป:
 * - 'sbiId'              - Mold SBI ID/Code
 * - 'moldCode'           - Mold code
 * - 'moldName'           - Mold name
 * - 'moldNumber'         - Mold number (ลูกที่)
 * - 'customer'           - Customer code
 * - 'productParts'       - Part numbers
 * - 'status'             - Current mold status
 * - 'revision'           - Revision number
 * - 'moldType'           - ประเภทของแม่พิมพ์
 * - 'moldSource'         - Mold Source
 * - 'cavity'             - Cavity count
 * - 'makerName'          - Mold Maker Name
 * - 'country'            - Country (extraAttributes.country)
 * - 'plate'              - Plate type
 * - 'gateType'           - Gate type
 * - 'delayGate'          - Delay Gate (boolean)
 * - 'hotRunner'          - Hot Runner (boolean)
 * - 'hotRunnerType'      - Hot Runner Type
 * - 'hotRunnerBrand'     - Hot Runner Brand
 * - 'qtyZoneHr'          - Q'ty Zone HR
 * - 'partMatGrade'       - Part Material Grade
 * - 'surfaceFinish'      - Surface Finish
 * - 'remarks'            - จุดที่ต้องระวัง
 *
 * Molds — จำนวนชอต:
 * - 'shotCount'                  - Shot count progress bar
 * - 'cycleShotCountDisplay'      - Cycle shot count value
 * - 'shotCountDisplay'           - Total shot count value
 * - 'physicalShotCountDisplay'   - Physical shot count from counter
 * - 'servicingAlertShotThreshold'- Servicing alert threshold
 * - 'servicingMaxShotThreshold'  - Servicing max threshold
 * - 'maxShotCount'               - Mold การันตี
 *
 * Molds — ขนาด:
 * - 'width'         - Width
 * - 'height'        - Height
 * - 'length'        - Length
 * - 'weight'        - Mold Weight
 * - 'machineSizeMin'- MC Size Min
 * - 'machineSizeMax'- MC Size Max
 * - 'locatedRingOd' - Located Ring O.D.
 * - 'nozzleSr'      - Nozzle SR
 *
 * Molds — Insert:
 * - 'insertEngraving'  - Insert/Engraving (boolean)
 * - 'insertChangeType' - ลักษณะการเปลี่ยน Insert
 *
 * Molds — ที่ตั้ง:
 * - 'currentLocation'  - ที่อยู่แม่พิมพ์
 * - 'storageLocation'  - ที่เก็บ
 * - 'assetNoCustomer'  - Asset NO Customer
 *
 * Molds — Actions:
 * - 'actions' - Action buttons
 *
 * Mold Orders:
 * - 'id' - Order number
 * - 'mold' - Mold code  
 * - 'moldNumber' - Mold number
 * - 'productParts' - Part numbers
 * - 'type' - Order type
 * - 'status' - Current status
 * - 'priority' - Priority level
 * - 'assignedTo' - Main assignee
 * - 'assignedTechnician' - Technician
 * - 'approvalTechnician' - Approval technician
 * - 'reporterConfirm' - Reporter confirmation
 * - 'createdAt' - Created date
 * - 'orderDetail' - Due date  
 * - 'operationDate' - Operation date
 * - 'supervisorReviewDate' - Review date
 * - 'reporterConfirmationDate' - Confirmation date
 * - 'shiftCompleted' - Production shift
 * - 'machineNumber' - Machine number
 * - 'operationDuration' - Duration
 * - 'rejectedReason' - Rejection reason (last state comment of a rejected order)
 * - 'rejectedBy' - Person who rejected the order
 * - 'rejectedState' - Workflow state the rejection happened in (getStatusText label)
 * - 'actions' - Action buttons
 */

// SEARCH FIELD CONFIGURATION FUNCTIONS

/**
 * Get search field configuration for molds from company-specific app state
 * @param companyCode - The client company code (sbi/sk)
 * @returns Company-specific search field configuration
 * @throws Error if company configuration is not found
 */
export const getMoldSearchFields = (companyCode: ClientCompanyCode): SearchFieldConfig[] => {
  const appState = getAppState(companyCode);
  return appState.search.moldSearchFields.availableSearchFields.filter(field => field.enabled !== false);
};

/**
 * Get default search field for molds from company-specific app state
 * @param companyCode - The client company code (sbi/sk) 
 * @returns Default search field id or 'all' if not specified
 */
export const getDefaultMoldSearchField = (companyCode: ClientCompanyCode): string => {
  const appState = getAppState(companyCode);
  return appState.search.moldSearchFields.defaultSearchField || 'all';
};

/**
 * Get search field configuration for mold orders from company-specific app state
 * @param companyCode - The client company code (sbi/sk)
 * @returns Company-specific search field configuration  
 * @throws Error if company configuration is not found
 */
export const getMoldOrderSearchFields = (companyCode: ClientCompanyCode): SearchFieldConfig[] => {
  const appState = getAppState(companyCode);
  return appState.search.moldOrderSearchFields.availableSearchFields.filter(field => field.enabled !== false);
};

/**
 * Get default search field for mold orders from company-specific app state
 * @param companyCode - The client company code (sbi/sk)
 * @returns Default search field id or 'all' if not specified
 */
export const getDefaultMoldOrderSearchField = (companyCode: ClientCompanyCode): string => {
  const appState = getAppState(companyCode);
  return appState.search.moldOrderSearchFields.defaultSearchField || 'all';
};
